import { StyleSheet, View } from 'react-native';

import { Button, Pill, Text } from '@/components/ui';
import { useTheme } from '@/theme';
import type { CourseAccent, CourseModule } from '@/content/courses';

const accentToVariant: Record<CourseAccent, 'wine' | 'verde' | 'ember' | 'gold'> = {
  wine: 'wine',
  verde: 'verde',
  ember: 'ember',
  gold: 'gold',
};

export function CourseRow({ module, accent }: { module: CourseModule; accent: CourseAccent }) {
  const theme = useTheme();
  const isHighlight = module.highlighted;
  const containerBg = isHighlight ? theme.colors.accent : theme.colors.bgSoft;
  const onBg = isHighlight ? theme.colors.invert : theme.colors.fg;
  const onBgSoft = isHighlight ? 'rgba(245,240,255,0.78)' : theme.colors.fgSoft;
  const ruleColor = isHighlight ? 'rgba(245,240,255,0.16)' : theme.colors.rule;

  return (
    <View
      style={[
        styles.row,
        {
          backgroundColor: containerBg,
          borderRadius: theme.radii.md,
          borderColor: ruleColor,
        },
      ]}
    >
      <View style={styles.head}>
        <View style={styles.codeStack}>
          <Pill variant={isHighlight ? 'invert' : accentToVariant[accent]}>
            {module.level}
          </Pill>
          <Text variant="meta" style={{ color: onBgSoft, marginTop: 6 }}>
            {module.code}
          </Text>
        </View>
        <View style={styles.titleCol}>
          <Text variant="h3" style={{ color: onBg }}>
            {module.title}
          </Text>
          <Text variant="body" style={{ color: onBgSoft, marginTop: 8, maxWidth: 560 }}>
            {module.body}
          </Text>
          <Text variant="meta" style={{ color: onBgSoft, marginTop: 12 }}>
            {module.duration} · {module.teacher}
          </Text>
        </View>
      </View>

      <View style={[styles.dates, { borderTopColor: ruleColor }]}>
        {module.abendkurs ? (
          <View style={[styles.dateBlock, { borderRightColor: ruleColor }]}>
            <Text variant="eyebrow" style={{ color: onBgSoft }}>
              Abendkurs
            </Text>
            <Text variant="h4" style={{ color: onBg, marginTop: 6 }}>
              {module.abendkurs.start}
            </Text>
            <Text variant="meta" style={{ color: onBgSoft, marginTop: 4 }}>
              {module.abendkurs.weeks}
            </Text>
          </View>
        ) : null}
        {module.weekender ? (
          <View style={[styles.dateBlock, { borderRightColor: ruleColor }]}>
            <Text variant="eyebrow" style={{ color: onBgSoft }}>
              Weekender
            </Text>
            <Text variant="h4" style={{ color: onBg, marginTop: 6 }}>
              {module.weekender.start}
            </Text>
            <Text variant="meta" style={{ color: onBgSoft, marginTop: 4 }}>
              {module.weekender.weeks}
            </Text>
          </View>
        ) : null}
        <View style={styles.priceBlock}>
          <Text variant="eyebrow" style={{ color: onBgSoft }}>
            Preis
          </Text>
          <Text
            italic
            variant="h3"
            style={{
              color: onBg,
              marginTop: 6,
              fontFamily: theme.fontFamily.displayItalic,
              fontStyle: 'italic',
            }}
          >
            {module.price}
          </Text>
          <View style={{ marginTop: 12 }}>
            <Button variant={isHighlight ? 'ember' : 'primary'} size="sm" trailingArrow href="/kontakt">
              {module.cta ?? 'Anmelden'}
            </Button>
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    borderWidth: 1,
    overflow: 'hidden',
  },
  head: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 24,
    padding: 24,
  },
  codeStack: {
    flexBasis: 120,
    flexGrow: 0,
  },
  titleCol: {
    flex: 1,
    minWidth: 240,
  },
  dates: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    borderTopWidth: 1,
  },
  dateBlock: {
    flex: 1,
    minWidth: 200,
    padding: 24,
    borderRightWidth: 1,
  },
  priceBlock: {
    flex: 1,
    minWidth: 200,
    padding: 24,
  },
});
